import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import LoadingSpinner from './LoadingSpinner';

/**
 * Component to display user's credit balance
 * @param {Object} props - Component props
 * @param {Object} props.credits - User's credits object with balance property
 * @param {boolean} props.showBuyButton - Whether to show the buy button
 * @param {string} props.size - Size of the component (small, medium, large)
 * @param {string} props.className - Additional CSS classes
 */
const CreditBalanceDisplay = ({
  credits,
  showBuyButton = false,
  size = 'medium',
  className = ''
}) => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();

  // Size classes for different component sizes
  const sizeClasses = {
    small: {
      container: 'px-2 py-1 text-xs',
      icon: 'h-3 w-3 mr-1',
      buyButton: 'ml-2 px-2 py-0.5 text-xs'
    },
    medium: {
      container: 'px-3 py-1.5 text-sm',
      icon: 'h-4 w-4 mr-1.5',
      buyButton: 'ml-3 px-3 py-1 text-sm'
    },
    large: {
      container: 'px-4 py-2 text-base',
      icon: 'h-5 w-5 mr-2',
      buyButton: 'ml-4 px-4 py-1.5'
    }
  };

  const classes = sizeClasses[size] || sizeClasses.medium;

  // Error state - credits object exists but has an error flag
  if (credits?.error) {
    return (
      <div className={`${semantic.errorBg} ${semantic.errorText} rounded-full flex items-center ${classes.container} ${className}`}>
        <span>{replace('Unable to load credits')}</span>
      </div>
    );
  }

  // Loading state - no credits yet or balance is undefined
  if (!credits || credits.balance === undefined || credits.balance === null) {
    return (
      <div className={`${semantic.sectionBg} ${semantic.formText} rounded-full flex items-center ${classes.container} ${className}`}>
        <LoadingSpinner size="xs" message="" />
        <span className="ml-2">{replace('Loading...')}</span>
      </div>
    );
  }

  // Low credits warning (less than 5)
  const isLowCredits = credits.balance < 5;

  return (
    <div className={`flex items-center ${className}`}>
      <div
        className={`
          rounded-full flex items-center
          ${isLowCredits
            ? `${semantic.errorBg} ${semantic.errorText}`
            : `${semantic.sectionBg} ${semantic.formText}`
          }
          ${classes.container}
        `}
        title={replace(`You have ${credits.balance.toFixed(1)} credits remaining`)}
        aria-label={replace(`Credit balance: ${credits.balance.toFixed(1)}`)}
      >
        
        {credits.balance.toFixed(1)}
      </div>

      {showBuyButton && (
        <Link
          to="/purchase"
          className={`
            ${semantic.actionButtonPurchase} rounded-full transition-colors
            ${classes.buyButton} inline-flex items-center justify-center
          `}
          aria-label={replace('Buy credits')}
        >
          {replace('Buy')}
        </Link>
      )}
    </div>
  );
};

CreditBalanceDisplay.propTypes = {
  credits: PropTypes.shape({
    balance: PropTypes.number
  }),
  showBuyButton: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  className: PropTypes.string
};

export default CreditBalanceDisplay;

